import { Button } from '@/components/ui/button';
import oktaAuth from '@/features/auth/services';
import {
  getCancelReasons,
  submitQuoteRequest,
} from '@/features/cancellationQuote/api';
import {
  CancellationDetailsForm,
  type CancellationDetailsFormSubmitValues,
} from '@/features/cancellationQuote/components/CancellationDetailsForm';
import type {
  CancelQuoteResult,
  CancelReason,
  GetCancelQuoteRequest,
} from '@/features/cancellationQuote/types';
import type { ContractDetail } from '@/features/contractSearch/types';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  createFileRoute,
  redirect,
  useLocation,
  useNavigate,
  useSearch,
} from '@tanstack/react-router';
import { formatISO } from 'date-fns';
import React from 'react';
import { toast } from 'sonner';
import { z, ZodError } from 'zod';

// Schema for URL search parameters
const requestQuoteSearchSchema = z.object({
  contracts: z
    .array(z.string())
    .min(1, 'At least one contract must be selected.'),
});

// Define the state we expect to receive for this route
export interface RequestQuoteRouteState {
  selectedContracts?: ContractDetail[]; // Make it optional to handle direct navigation/refresh
}

export const Route = createFileRoute('/request-quote')({
  validateSearch: (search) => requestQuoteSearchSchema.parse(search),
  // No direct `validateState` or `initialState` here for now, will rely on TS and conditional checks
  beforeLoad: async ({ location }) => {
    const isAuthenticated = await oktaAuth.isAuthenticated();
    if (!isAuthenticated) {
      oktaAuth.signInWithRedirect({ originalUri: location.href });
      throw redirect({
        to: '/',
      });
    }
    return {};
  },
  component: RequestQuotePage,
  errorComponent: RequestQuoteErrorComponent,
});

// Error component for this route
function RequestQuoteErrorComponent({ error }: { error: Error | ZodError }) {
  const navigate = useNavigate();
  let errorMessage = 'An unexpected error occurred.';
  let errorDetails = error.message;

  if (error instanceof ZodError) {
    // Check if it's a search param error or state validation error
    if (error.errors.some((e) => e.path.includes('contracts'))) {
      // Likely search param error
      errorMessage = 'Invalid request parameters.';
      const contractError = error.errors.find((e) =>
        e.path.includes('contracts')
      );
      errorDetails = contractError
        ? `Contracts: ${contractError.message}`
        : error.message;
    } else if (error.errors.some((e) => e.path.includes('selectedContracts'))) {
      // Likely state error
      errorMessage = 'Missing contract details.';
      errorDetails =
        'Could not load details for the selected contracts. Please try again from search.';
    } else {
      errorMessage = 'Validation Error';
      errorDetails = error.errors
        .map((e) => `${e.path.join('.')}: ${e.message}`)
        .join('; ');
    }
  } else if (error.message.includes('validateSearch')) {
    errorMessage = 'Missing required information to proceed.';
    errorDetails =
      'Please ensure you have selected contracts and their details are available.';
  }

  return (
    <div className='container mx-auto p-4 text-center'>
      <h1 className='text-2xl font-bold text-destructive mb-4'>
        Error Loading Page
      </h1>
      <p className='text-lg mb-2'>{errorMessage}</p>
      <p className='text-sm text-muted-foreground mb-4'>{errorDetails}</p>
      <Button onClick={() => navigate({ to: '/search' })}>
        Go Back to Search
      </Button>
    </div>
  );
}

export function RequestQuotePage() {
  const navigate = useNavigate();
  const search = useSearch({ from: Route.id });
  const location = useLocation();
  const routeState = location.state as RequestQuoteRouteState | undefined;

  // Determine if vehicle mileage is required
  // vehicle mileage is required if the user wants a quote for any Service contract
  const requiresVehicleMileage = React.useMemo(() => {
    if (routeState && routeState.selectedContracts) {
      return routeState.selectedContracts.some(
        (contract) =>
          contract.productTypeCode === 'VSC' ||
          contract.productTypeCode === 'MNT'
      );
    }
    return false; // Default to false if no contract details are available
  }, [routeState]);

  const {
    data: cancelReasons,
    error: cancelReasonsError,
    isError: isCancelReasonsError,
  } = useQuery<CancelReason[], Error>({
    queryKey: ['cancelReasons'],
    queryFn: () => getCancelReasons(),
  });

  React.useEffect(() => {
    if (isCancelReasonsError && cancelReasonsError) {
      toast.error(cancelReasonsError.message);
    }
  }, [isCancelReasonsError, cancelReasonsError]);

  const quoteMutation = useMutation<
    CancelQuoteResult[],
    Error,
    GetCancelQuoteRequest
  >({
    mutationFn: submitQuoteRequest,
    onSuccess: (data, variables) => {
      if (!routeState?.selectedContracts || !cancelReasons) {
        console.error(
          'Missing selectedContracts or cancelReasons in route state for navigation to results page'
        );
        toast.error(
          'Could not proceed to results page: missing critical data.'
        );
        return;
      }
      navigate({
        to: '/quote-results',
        state: {
          quoteResults: data,
          submittedQuoteRequest: variables,
          selectedContracts: routeState.selectedContracts,
          cancelReasons: cancelReasons,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any,
      });
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const handleFormSubmit = (
    formValues: CancellationDetailsFormSubmitValues
  ) => {
    if (!search.contracts || search.contracts.length === 0) {
      console.error('No contracts selected for quote request.');
      // Potentially show a user-facing error here
      return;
    }
    // It's possible routeState.selectedContracts is undefined if the user navigated here directly
    // or if state was lost. Handle this gracefully.
    if (
      !routeState ||
      !routeState.selectedContracts ||
      routeState.selectedContracts.length === 0
    ) {
      console.warn(
        'Contract details (state) not available for selected contracts. Proceeding with numbers only for submission, but full details might be missing on results page if not handled.'
      );
      // Potentially, here you could try to re-fetch contract details if critical for the quote submission itself,
      // or show a more specific error to the user, or disable submission.
      // For now, we allow submission but the results page will need to handle missing details.
    }

    const payload: GetCancelQuoteRequest = {
      cancelDate: formatISO(formValues.cancelDate, { representation: 'date' }),
      cancelMileage: formValues.vehicleMileage,
      cancelReasonId: Number(formValues.cancelReasonId),
      contractNumbers: search.contracts,
    };
    quoteMutation.mutate(payload);
  };

  return (
    <div className='container mx-auto p-4'>
      <h1 className='text-2xl font-bold mb-4'>Request Cancellation Quote</h1>

      <div className='mb-6 p-4 border rounded-md bg-muted'>
        <h2 className='text-lg font-semibold mb-2'>
          Selected Contracts for Quotation:
        </h2>
        {routeState &&
        routeState.selectedContracts &&
        routeState.selectedContracts.length > 0 ? (
          <ul className='list-disc pl-5'>
            {routeState.selectedContracts.map((contract: ContractDetail) => (
              <li key={contract.contractNumber}>
                {contract.contractNumber} - {contract.productTypeName} (VIN:{' '}
                {contract.vehicleInfo.vin})
              </li>
            ))}
          </ul>
        ) : search.contracts && search.contracts.length > 0 ? (
          // Fallback if state wasn't passed correctly but search params were
          <ul className='list-disc pl-5'>
            {search.contracts.map((contractNumber: string) => (
              <li key={contractNumber}>{contractNumber}</li>
            ))}
          </ul>
        ) : (
          <p>
            No contracts selected. Please go back to search and select
            contracts.
          </p>
        )}
      </div>

      <CancellationDetailsForm
        onSubmit={handleFormSubmit}
        isSubmitting={quoteMutation.isPending}
        requiresVehicleMileage={requiresVehicleMileage}
      />

      {quoteMutation.error && (
        <div
          role='alert'
          className='mt-4 p-3 text-destructive bg-destructive/10 border border-destructive rounded-md'
        >
          <p>
            <strong>Error submitting quote request:</strong>
          </p>
          <p>{quoteMutation.error.message}</p>
        </div>
      )}
    </div>
  );
}
